/*
 * BullionBox theme — Vanilla JS for interactive bits.
 * Most state is handled by Alpine.js inline directives (x-data, x-show, etc.).
 * This file holds the bits that don't fit cleanly into Alpine:
 *   - Live spot price ticker (Coinbase public API — CORS-enabled, no key required)
 *   - Sticky-on-scroll behavior
 *   - Exit intent popup logic
 *   - Toast notifications (used by form submit success)
 */

(function () {
  "use strict";

  // ──────────────────────────────────────────────────────────────────
  // Spot Price Ticker — fetches Gold/Silver/Platinum/Palladium/Copper
  // every 60 seconds and updates the marquee.
  //
  // Source: Coinbase public spot-price API.
  //   https://api.coinbase.com/v2/prices/{pair}/spot
  // The API returns Access-Control-Allow-Origin: * so it works directly
  // from the browser with zero CORS errors.
  // Copper is not offered by Coinbase — it uses the hardcoded fallback.
  // ──────────────────────────────────────────────────────────────────
  const SPOT_SYMBOLS = [
    { label: "Gold",      coinbase: "XAU-USD", fallback: 4570.56, change:  18.40, pct:  0.40 },
    { label: "Silver",    coinbase: "XAG-USD", fallback:   78.18, change:  -0.34, pct: -0.44 },
    { label: "Platinum",  coinbase: "XPT-USD", fallback: 1963.58, change:  11.39, pct:  0.58 },
    { label: "Palladium", coinbase: "XPD-USD", fallback: 1387.36, change: -11.80, pct: -0.84 },
    // { label: "Copper",    coinbase: null,       fallback:    4.52, change:   0.03, pct:  0.67 },
  ];

  // Baseline price from the first successful fetch — delta is always relative
  // to page-load price so the change never collapses to ~0 between 60s ticks.
  var _initialPrices = {};
  // Last confirmed non-zero price per pair — used as fallback when API returns
  // 0 or an error, so the ticker never shows $0.00.
  var _lastGoodPrices = {};

  async function fetchSpotPrices() {
    const results = await Promise.allSettled(
      SPOT_SYMBOLS.map(async (s) => {
        if (!s.coinbase) {
          return { label: s.label, price: s.fallback, change: s.change, pct: s.pct };
        }
        const url = "https://api.coinbase.com/v2/prices/" + s.coinbase + "/spot";
        const resp = await fetch(url);
        if (!resp.ok) throw new Error("HTTP " + resp.status);
        const data = await resp.json();
        const price = parseFloat(data.data.amount);
        // Treat 0 or NaN as invalid — Coinbase returns amount:"0" when rate-limited
        // with HTTP 200, which would otherwise render as $0.00.
        if (isNaN(price) || price <= 0) throw new Error("Invalid price");

        // Cache this as the last confirmed good price.
        _lastGoodPrices[s.coinbase] = price;

        // On first fetch store the baseline; on subsequent fetches compute
        // delta vs that baseline so the change reflects movement since page load.
        var base = _initialPrices[s.coinbase];
        if (base == null) _initialPrices[s.coinbase] = price;
        var change = base != null ? price - base : s.change;
        var pct    = base != null && base > 0 ? (change / base) * 100 : s.pct;

        return { label: s.label, price, change, pct };
      })
    );

    return SPOT_SYMBOLS.map(function (s, i) {
      var r = results[i];
      if (r.status === "fulfilled") return r.value;
      // API failed — use last known good price if we have one, else static fallback.
      var price = _lastGoodPrices[s.coinbase] || s.fallback;
      var base  = _initialPrices[s.coinbase];
      var change = base != null ? price - base : s.change;
      var pct    = base != null && base > 0 ? (change / base) * 100 : s.pct;
      return { label: s.label, price, change, pct };
    });
  }

  const SVG_TRENDING_UP   = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>';
  const SVG_TRENDING_DOWN = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg>';
  const SVG_MINUS         = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>';

  function renderPrice({ label, price, change, pct }) {
    const isUp = change > 0;
    const isDown = change < 0;
    const trendColor = isUp ? "text-[#4ade80]" : isDown ? "text-[#f87171]" : "text-[#FAF8F5]/60";
    const trendIcon  = isUp ? SVG_TRENDING_UP : isDown ? SVG_TRENDING_DOWN : SVG_MINUS;
    return `
      <span class="inline-flex items-center gap-1.5 mx-4 whitespace-nowrap">
        <span class="text-[11px] font-sans font-medium text-[#FAF8F5]/50 uppercase">${label}</span>
        <span class="text-[11px] font-mono font-semibold text-[#CBA960]">
          $${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
        <span class="inline-flex items-center gap-0.5 text-[10px] font-mono ${trendColor}">
          ${trendIcon}${isUp ? "+" : ""}${pct.toFixed(2)}%
        </span>
        <span class="text-[#FAF8F5]/15 ml-3">•</span>
      </span>
    `;
  }

  function renderMobilePrice({ label, price }) {
    const formatted = price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `<div class="flex items-center gap-1.5">
      <span class="text-xs font-sans font-medium text-[#191511]/50 uppercase">${label}</span>
      <span class="text-xs font-mono font-semibold" style="color: var(--color-gold)">$${formatted}</span>
    </div>`;
  }

  async function updateTicker() {
    const contentNodes = document.querySelectorAll("[data-spot-ticker]");
    if (contentNodes.length === 0) return;
    try {
      const prices = await fetchSpotPrices();
      const html = prices.map(renderPrice).join("");
      // Each [data-spot-ticker] gets the FULL set of prices. The header.liquid
      // renders two such nodes inside one .marquee-track so the CSS -50%
      // translate produces a seamless loop. No JS width measurement needed.
      contentNodes.forEach((el) => {
        el.innerHTML = html;
      });
      // Measure first content set after DOM update and set exact px offset —
      // matches React's useLayoutEffect scrollWidth approach for identical speed.
      requestAnimationFrame(function() {
        var track = document.querySelector('.marquee-track');
        if (track && contentNodes[0]) {
          var w = contentNodes[0].scrollWidth;
          if (w > 0) track.style.setProperty('--marquee-offset', '-' + w + 'px');
        }
      });
      // Update any [data-live-gold-price] stat elements (e.g. Market Context section)
      const goldPrice = prices.find(function(p) { return p.label === "Gold"; });
      if (goldPrice) {
        const formatted = "$" + goldPrice.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        document.querySelectorAll("[data-live-gold-price]").forEach(function(el) {
          el.textContent = formatted;
        });
      }
      // Update mobile spot price mini-grid (Gold/Silver/Platinum/Palladium only)
      const mobileEl = document.getElementById("bb-mobile-spot-prices");
      if (mobileEl) {
        const mobileHtml = prices
          .filter((p) => ["Gold", "Silver", "Platinum", "Palladium"].includes(p.label))
          .map(renderMobilePrice)
          .join("");
        mobileEl.innerHTML = mobileHtml;
      }
    } catch {
      /* keep fallback prices already in DOM */
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Toast notifications — minimal, slides in from top-right.
  // window.bbToast(message, type) — type: 'success' | 'error' | 'info'
  // ──────────────────────────────────────────────────────────────────
  function ensureToastContainer() {
    let el = document.getElementById("bb-toast-root");
    if (el) return el;
    el = document.createElement("div");
    el.id = "bb-toast-root";
    el.className = "fixed top-4 right-4 z-[200] flex flex-col gap-2 pointer-events-none";
    document.body.appendChild(el);
    return el;
  }

  window.bbToast = function (message, type) {
    const root = ensureToastContainer();
    const colors = {
      success: "bg-[#191511] text-[#FAF8F5] border-[#CBA960]/40",
      error: "bg-red-600 text-white border-red-700",
      info: "bg-[#191511] text-[#FAF8F5] border-[#191511]",
    };
    const tone = colors[type] || colors.info;
    const toast = document.createElement("div");
    toast.className = `pointer-events-auto rounded-sm border px-4 py-3 text-sm font-sans shadow-lg transition-all duration-200 ${tone}`;
    toast.style.opacity = "0";
    toast.style.transform = "translateX(20px)";
    toast.textContent = message;
    root.appendChild(toast);
    requestAnimationFrame(() => {
      toast.style.opacity = "1";
      toast.style.transform = "translateX(0)";
    });
    setTimeout(() => {
      toast.style.opacity = "0";
      toast.style.transform = "translateX(20px)";
      setTimeout(() => toast.remove(), 200);
    }, 4000);
  };

  // ──────────────────────────────────────────────────────────────────
  // Scroll-triggered fade-in-up — mirrors React framer-motion useInView.
  // Adds .is-visible to .fade-in-up elements when they enter the viewport.
  // animation-delay on each element produces the staggered entrance effect.
  // ──────────────────────────────────────────────────────────────────
  let fadeObserver = null;
  function getFadeObserver() {
    if (fadeObserver) return fadeObserver;
    fadeObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          fadeObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    return fadeObserver;
  }

  function observeFadeElements(root) {
    const scope = root || document;
    scope.querySelectorAll(".fade-in-up:not(.is-visible)").forEach((el) => {
      getFadeObserver().observe(el);
    });
  }

  // Re-observe when sections are added/reloaded in the theme editor
  document.addEventListener("shopify:section:load", (e) => observeFadeElements(e.target));
  document.addEventListener("shopify:section:reorder", () => observeFadeElements(document));
  document.addEventListener("shopify:block:select", (e) => {
    observeFadeElements(e.target.closest("[id^='shopify-section-']") || document);
  });

  // ──────────────────────────────────────────────────────────────────
  // Animated counters — count from 0 to target when scrolled into view.
  // Apply to any element with [data-animate-counter] and a data-target
  // (numeric) and optional data-suffix.
  // ──────────────────────────────────────────────────────────────────
  // Reusable IntersectionObserver — instantiated once, re-used for every
  // counter (including ones added later by the Shopify theme editor).
  let counterObserver = null;
  function getCounterObserver() {
    if (counterObserver) return counterObserver;
    counterObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          if (el.dataset.animated) return;
          el.dataset.animated = "1";

          const target = parseFloat(el.dataset.target || "0");
          const suffix = el.dataset.suffix || "";

          // No animation needed for 0/invalid targets — just render
          if (!target || isNaN(target)) {
            el.textContent = "0" + suffix;
            counterObserver.unobserve(el);
            return;
          }

          const duration = 2000;
          const startTime = performance.now();

          function tick(now) {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const current = Math.floor(progress * target);
            el.textContent = current.toLocaleString() + suffix;
            if (progress < 1) {
              requestAnimationFrame(tick);
            } else {
              el.textContent = target.toLocaleString() + suffix;
            }
          }
          requestAnimationFrame(tick);

          counterObserver.unobserve(el);
        });
      },
      { threshold: 0.3 }
    );
    return counterObserver;
  }

  function observeCountersIn(root) {
    const scope = root || document;
    const counters = scope.querySelectorAll("[data-animate-counter]:not([data-animated])");
    if (counters.length === 0) return;
    const obs = getCounterObserver();
    counters.forEach((el) => obs.observe(el));
  }

  function initCounters() {
    observeCountersIn(document);
  }

  // Shopify theme editor: re-init counters whenever a section is added,
  // re-rendered, or its settings change. Without this, edits show "0".
  document.addEventListener("shopify:section:load", (event) => {
    observeCountersIn(event.target);
  });
  document.addEventListener("shopify:section:reorder", () => {
    observeCountersIn(document);
  });
  document.addEventListener("shopify:block:select", (event) => {
    observeCountersIn(event.target.closest("[id^='shopify-section-']") || document);
  });

  // ──────────────────────────────────────────────────────────────────
  // Embla Carousels — initialized on any [data-embla] element with
  // autoplay (4s delay, pause on hover/interaction). Matches the
  // React shadcn Carousel + embla-autoplay setup.
  // ──────────────────────────────────────────────────────────────────
  function initEmblaCarousels() {
    if (typeof window.EmblaCarousel === "undefined") return;

    const carousels = document.querySelectorAll("[data-embla]:not([data-embla-ready])");
    carousels.forEach((root) => {
      root.dataset.emblaReady = "1";

      const viewport = root.querySelector(".embla__viewport");
      if (!viewport) return;

      const plugins = [];
      if (root.dataset.emblaAutoplay !== "false" && typeof window.EmblaCarouselAutoplay !== "undefined") {
        const delay = parseInt(root.dataset.emblaDelay || "4000", 10);
        plugins.push(
          window.EmblaCarouselAutoplay({
            delay,
            stopOnInteraction: true,
            stopOnMouseEnter: true,
          })
        );
      }

      const options = {
        align: root.dataset.emblaAlign || "start",
        loop: root.dataset.emblaLoop !== "false",
        slidesToScroll: 1,
      };

      const embla = window.EmblaCarousel(viewport, options, plugins);
      const autoplayPlugin = plugins[0];

      const prevBtn = root.querySelector(".embla__prev");
      const nextBtn = root.querySelector(".embla__next");
      let busy = false;
      function scrollDebounced(fn) {
        if (busy) return;
        busy = true;
        if (autoplayPlugin) autoplayPlugin.stop();
        fn();
        setTimeout(() => { busy = false; }, 300);
      }
      if (prevBtn) prevBtn.addEventListener("click", () => scrollDebounced(() => embla.scrollPrev()));
      if (nextBtn) nextBtn.addEventListener("click", () => scrollDebounced(() => embla.scrollNext()));
    });
  }

  // Re-init on Shopify theme editor section reload
  document.addEventListener("shopify:section:load", () => {
    setTimeout(initEmblaCarousels, 50);
  });

  // ──────────────────────────────────────────────────────────────────
  // Init on DOM ready
  // ──────────────────────────────────────────────────────────────────
  function init() {
    updateTicker();
    let tickerInterval = setInterval(updateTicker, 60_000);
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        clearInterval(tickerInterval);
      } else {
        updateTicker();
        tickerInterval = setInterval(updateTicker, 60_000);
      }
    });
    observeFadeElements(document);
    initCounters();
    initEmblaCarousels();
    document.addEventListener('embla:ready', initEmblaCarousels);

    // Clean up ?contact_posted param from URL after Shopify redirect (toast is handled inline per section)
    const params = new URLSearchParams(window.location.search);
    if (params.get("contact_posted") === "true") {
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (params.get("customer_posted") === "true") {
      window.bbToast("You're on the list! Check your inbox.", "success");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
